import React, { FC, KeyboardEvent, useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@apollo/client';
import { Box, Button, IconButton, InputAdornment, List, ListItem, Paper, TextField, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { DragDropContext, Draggable, Droppable, DropResult } from '@hello-pangea/dnd';
import { resetPauseReasonsTemplate, savePauseReasonsTemplate } from 'apollo/templates/organization/mutation.graphql';
import { usePauseReasons } from 'hooks/usePauseReasons';
import ConfirmDialog from 'components/shared/confirm-dialog/confirm-dialog.component';
import { AppToasterContext } from 'components/shared/app-toaster/app-toaster-context';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import SkeletonTable from 'components/shared/skeleton/skeleton-table.component';

const PAUSE_REASON_MAX_LENGTH = 100;

type Row = { id: string; text: string };

let rowIdCounter = 0;
const toRows = (reasons: string[]): Row[] => reasons.map((text) => ({ id: `pause-reason-${rowIdCounter++}`, text }));

const isDuplicate = (rows: Row[], text: string, exceptId?: string) =>
    rows.some((row) => row.id !== exceptId && row.text.trim().toLocaleLowerCase() === text.trim().toLocaleLowerCase());

/**
 * Lab-managed pause reasons, edited like the department workflow-step list:
 * drag to reorder, delete per row, add at the bottom. Every change is saved
 * straight away; a renamed row is saved when the field loses focus.
 */
const PauseReasons: FC = () => {
    const { t: translate } = useTranslation(['lab-settings', 'common']);
    const { setAppToasterContent } = useContext(AppToasterContext);
    const { showBoundary } = useDenthubErrorBoundary();
    const { pauseReasons, loading, refetch } = usePauseReasons();

    const [rows, setRows] = useState<Row[]>([]);
    const [newReason, setNewReason] = useState('');
    const [resetConfirmOpen, setResetConfirmOpen] = useState(false);
    const [rowToDelete, setRowToDelete] = useState<Row | null>(null);
    const savedRows = useRef<Row[]>([]);

    const [savePauseReasons] = useMutation(savePauseReasonsTemplate);
    const [resetPauseReasons] = useMutation(resetPauseReasonsTemplate);

    const loadedKey = loading ? null : JSON.stringify(pauseReasons);
    useEffect(() => {
        if (loadedKey === null) return;
        // Keep the row ids when the refetch after our own save matches, so focus stays in the field being edited.
        const sameTexts =
            savedRows.current.length === pauseReasons.length &&
            savedRows.current.every((row, index) => row.text === pauseReasons[index]);
        if (sameTexts) return;
        const loaded = toRows(pauseReasons);
        savedRows.current = loaded;
        setRows(loaded);
    }, [loadedKey]);

    const save = async (nextRows: Row[]) => {
        setRows(nextRows);
        try {
            await savePauseReasons({ variables: { reasons: nextRows.map((row) => row.text.trim()) } });
            savedRows.current = nextRows;
            await refetch();
        } catch (error) {
            setRows(savedRows.current);
            showBoundary(error);
        }
    };

    const handleDragEnd = ({ source, destination }: DropResult) => {
        if (!destination || source.index === destination.index) return;
        const reordered = [...rows];
        const [moved] = reordered.splice(source.index, 1);
        reordered.splice(destination.index, 0, moved);
        save(reordered);
    };

    const handleConfirmDelete = async () => {
        if (!rowToDelete) return;
        await save(rows.filter((row) => row.id !== rowToDelete.id));
        setRowToDelete(null);
    };

    const handleRenameBlur = (row: Row) => {
        const previous = savedRows.current.find(({ id }) => id === row.id);
        const text = row.text.trim();
        if (!text || isDuplicate(rows, text, row.id)) {
            // An empty or duplicate name is not saved; the row goes back to its saved name.
            setRows((current) => current.map((r) => (r.id === row.id ? { ...r, text: previous?.text ?? '' } : r)));
            return;
        }
        if (text === previous?.text) return;
        save(rows.map((r) => (r.id === row.id ? { ...r, text } : r)));
    };

    const trimmedNewReason = newReason.trim();
    const newReasonIsDuplicate = !!trimmedNewReason && isDuplicate(rows, trimmedNewReason);

    const handleAdd = () => {
        if (!trimmedNewReason || newReasonIsDuplicate) return;
        save([...rows, ...toRows([trimmedNewReason])]);
        setNewReason('');
    };

    const handleReset = async () => {
        try {
            await resetPauseReasons();
            await refetch();
            setAppToasterContent({
                isOpen: true,
                severity: 'success',
                message: translate('lab-settings:PAUSE_REASONS_RESET_DONE'),
            });
        } catch (error) {
            showBoundary(error);
        } finally {
            setResetConfirmOpen(false);
        }
    };

    if (loading && !rows.length) {
        return <SkeletonTable columns={[{ type: 'text', width: 300 }]} rows={4} />;
    }

    return (
        <Box maxWidth={600}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="body2" color="textSecondary">
                    {translate('lab-settings:PAUSE_REASONS_DESCRIPTION')}
                </Typography>
                <Button
                    onClick={() => setResetConfirmOpen(true)}
                    sx={{ flexShrink: 0, ml: 2 }}
                    data-test="pause-reasons.reset-btn"
                >
                    {translate('lab-settings:PAUSE_REASONS_RESET')}
                </Button>
            </Box>
            <Paper variant="outlined">
                <DragDropContext onDragEnd={handleDragEnd}>
                    <Droppable droppableId="pause-reasons-droppable">
                        {(provided) => (
                            <List dense ref={provided.innerRef} {...provided.droppableProps}>
                                {rows.map((row, index) => (
                                    <Draggable key={row.id} draggableId={row.id} index={index}>
                                        {(dragProvided, dragSnapshot) => (
                                            <ListItem
                                                ref={dragProvided.innerRef}
                                                {...dragProvided.draggableProps}
                                                secondaryAction={
                                                    <IconButton
                                                        onClick={() =>
                                                            setRowToDelete(
                                                                savedRows.current.find(({ id }) => id === row.id) ??
                                                                    row,
                                                            )
                                                        }
                                                        edge="end"
                                                        aria-label={translate('common:DELETE')}
                                                        data-test="pause-reasons.delete-btn"
                                                    >
                                                        <DeleteIcon />
                                                    </IconButton>
                                                }
                                                sx={{
                                                    background: dragSnapshot.isDragging ? '#f3f3f3' : undefined,
                                                    mb: 1,
                                                }}
                                            >
                                                <Box
                                                    {...dragProvided.dragHandleProps}
                                                    sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        mr: 2,
                                                        cursor: 'grab',
                                                        color: dragSnapshot.isDragging
                                                            ? 'primary.main'
                                                            : 'action.active',
                                                    }}
                                                >
                                                    <DragIndicatorIcon />
                                                </Box>
                                                <TextField
                                                    fullWidth
                                                    value={row.text}
                                                    inputProps={{ maxLength: PAUSE_REASON_MAX_LENGTH }}
                                                    onChange={(e) =>
                                                        setRows((current) =>
                                                            current.map((r) =>
                                                                r.id === row.id ? { ...r, text: e.target.value } : r,
                                                            ),
                                                        )
                                                    }
                                                    onBlur={() => handleRenameBlur(row)}
                                                    onKeyDown={(e: KeyboardEvent<HTMLDivElement>) => {
                                                        if (e.key === 'Enter') (e.target as HTMLElement).blur();
                                                    }}
                                                    sx={{ mr: 4 }}
                                                    data-test="pause-reasons.reason-txt"
                                                />
                                            </ListItem>
                                        )}
                                    </Draggable>
                                ))}
                                {provided.placeholder}
                                {!rows.length && (
                                    <ListItem>
                                        <Typography variant="body2" color="textSecondary">
                                            {translate('lab-settings:NO_PAUSE_REASONS')}
                                        </Typography>
                                    </ListItem>
                                )}
                                <ListItem>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        sx={{ mt: 1 }}
                                        label={translate('lab-settings:ADD_PAUSE_REASON')}
                                        value={newReason}
                                        inputProps={{ maxLength: PAUSE_REASON_MAX_LENGTH }}
                                        onChange={(e) => setNewReason(e.target.value)}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter') handleAdd();
                                        }}
                                        error={newReasonIsDuplicate}
                                        helperText={
                                            newReasonIsDuplicate
                                                ? translate('lab-settings:PAUSE_REASON_ALREADY_EXISTS')
                                                : undefined
                                        }
                                        InputProps={{
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <IconButton
                                                        onClick={handleAdd}
                                                        disabled={!trimmedNewReason || newReasonIsDuplicate}
                                                        edge="end"
                                                        aria-label={translate('lab-settings:ADD_PAUSE_REASON')}
                                                        data-test="pause-reasons.add-btn"
                                                    >
                                                        <AddIcon />
                                                    </IconButton>
                                                </InputAdornment>
                                            ),
                                        }}
                                        data-test="pause-reasons.new-reason-txt"
                                    />
                                </ListItem>
                            </List>
                        )}
                    </Droppable>
                </DragDropContext>
            </Paper>
            <ConfirmDialog
                open={!!rowToDelete}
                handleClose={() => setRowToDelete(null)}
                handleConfirm={handleConfirmDelete}
                dialogTitle={translate('lab-settings:PAUSE_REASON_DELETE_CONFIRM_TITLE')}
                dialogContent={translate('lab-settings:PAUSE_REASON_DELETE_CONFIRM_TEXT', {
                    reason: rowToDelete?.text,
                    interpolation: { escapeValue: false },
                })}
                confirmBtnLabel={translate('common:DELETE')}
                declineBtnLabel={translate('common:CANCEL')}
            />
            <ConfirmDialog
                open={resetConfirmOpen}
                handleClose={() => setResetConfirmOpen(false)}
                handleConfirm={handleReset}
                dialogTitle={translate('lab-settings:PAUSE_REASONS_RESET_CONFIRM_TITLE')}
                dialogContent={translate('lab-settings:PAUSE_REASONS_RESET_CONFIRM_TEXT')}
                confirmBtnLabel={translate('lab-settings:PAUSE_REASONS_RESET')}
                declineBtnLabel={translate('common:CANCEL')}
            />
        </Box>
    );
};

export default PauseReasons;
